import { Link } from '@/i18n/navigation'
import { IButtonProps } from '@/lib/types/button.types'
import { cn } from '@/lib/utils'
import { Slot } from 'radix-ui'

import { ClientSmoothScrollButton } from './client-smooth-scroll-button'

export function Button({
  id,
  label,
  icon,
  iconPosition = 'left',
  type = 'button',
  variant = 'primary',
  className = '',
  asChild = false,
  disabled = false,
  href,
  target,
  onClick,
  smoothScroll = true,
  scrollOffset = 120,
  ariaLabel,
  ...props
}: IButtonProps) {
  const Component = asChild ? Slot.Root : href ? 'span' : 'button'

  const isIconOnly = !label && Boolean(icon)

  const baseClasses = cn('base-btn', isIconOnly ? 'p-2' : 'py-5 px-8')

  const buttonVariants = {
    primary:
      'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700 disabled:bg-primary-200 text-primary-500',
    secondary:
      'bg-primary-500 hover:bg-primary-700 active:bg-primary-800 text-white',
    tertiary: 'bg-gray-500 hover:bg-tertiary-800 text-primary',
    ghost: 'bg-none border-0 hover:scale-[1.05] text-primary'
  }

  const content = (
    <>
      {!isIconOnly && icon && iconPosition === 'left' && (
        <span className="mr-2 inline-flex items-center">{icon}</span>
      )}
      {isIconOnly ? icon : label}
      {!isIconOnly && icon && iconPosition === 'right' && (
        <span className="ml-2 inline-flex items-center">{icon}</span>
      )}
    </>
  )

  if (href && href.includes('#') && smoothScroll) {
    return (
      <ClientSmoothScrollButton
        id={id}
        label={label}
        icon={icon}
        iconPosition={iconPosition}
        variant={variant}
        className={className}
        disabled={disabled}
        href={href}
        scrollOffset={scrollOffset}
        onClick={onClick}
        {...props}
      />
    )
  }

  if (href && !asChild) {
    return (
      <Link
        href={href}
        {...props}
        target={target}
        aria-label={ariaLabel || label}
      >
        <Component
          id={id}
          disabled={disabled}
          className={cn(baseClasses, buttonVariants[variant], className)}
        >
          {content}
        </Component>
      </Link>
    )
  }

  return (
    <Component
      id={id}
      onClick={onClick}
      type={type}
      disabled={disabled}
      {...props}
      className={cn(baseClasses, buttonVariants[variant], className)}
    >
      {content}
    </Component>
  )
}
